import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, InputBase, ClickAwayListener } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { GT_COLORS } from './constants';

// ─── Types ──────────────────────────────────────────────────

export interface SearchResultPerson {
    id: string;
    name: string;
    subtitle: string;
    teamName?: string;
    groupName?: string;
    initials: string;
    avatarColor: string;
    unassigned?: boolean;
}

export interface SearchResultTeam {
    id: string;
    name: string;
    memberCount: number;
    groupName: string;
}

export interface SearchResultGroup {
    id: string;
    name: string;
    teamCount: number;
}

interface SearchDropdownProps {
    placeholder?: string;
    width?: string | number;
    groups?: SearchResultGroup[];
    people: SearchResultPerson[];
    teams: SearchResultTeam[];
    loading?: boolean;
    onSearch: (query: string) => void;
    onSelectGroup?: (group: SearchResultGroup) => void;
    onSelectPerson?: (person: SearchResultPerson) => void;
    onSelectTeam?: (team: SearchResultTeam) => void;
}

// ─── Component ──────────────────────────────────────────────

export const SearchDropdown: React.FC<SearchDropdownProps> = ({
    placeholder = 'Search groups, teams, or people...',
    width = 400,
    groups = [],
    people,
    teams,
    loading,
    onSearch,
    onSelectGroup,
    onSelectPerson,
    onSelectTeam,
}) => {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const hasResults = people.length > 0 || teams.length > 0;
    const showDropdown = open && query.length >= 2;

    useEffect(() => {
        const timer = setTimeout(() => {
            if (query.length >= 2) onSearch(query);
        }, 250);
        return () => clearTimeout(timer);
    }, [query, onSearch]);

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box sx={{ position: 'relative', flex: `0 0 ${typeof width === 'number' ? `${width}px` : width}` }}>
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <SearchIcon
                        sx={{
                            position: 'absolute',
                            left: '10px',
                            fontSize: '18px',
                            color: GT_COLORS.textFaint,
                            pointerEvents: 'none',
                        }}
                    />
                    <InputBase
                        inputRef={inputRef}
                        placeholder={placeholder}
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setOpen(true);
                        }}
                        onFocus={() => query.length >= 2 && setOpen(true)}
                        sx={{
                            width: '100%',
                            border: `1px solid ${GT_COLORS.borderInput}`,
                            borderRadius: '8px',
                            padding: '8px 12px 8px 36px',
                            fontSize: '14px',
                            backgroundColor: GT_COLORS.cardBg,
                            '&.Mui-focused': {
                                borderColor: GT_COLORS.primary,
                                boxShadow: `0 0 0 3px rgba(32,111,165,.1)`,
                            },
                        }}
                    />
                </Box>

                {showDropdown && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 'calc(100% + 4px)',
                            left: 0,
                            right: 0,
                            backgroundColor: GT_COLORS.cardBg,
                            border: `1px solid ${GT_COLORS.border}`,
                            borderRadius: '10px',
                            boxShadow: '0 8px 24px rgba(0,0,0,.12)',
                            zIndex: 80,
                            maxHeight: '380px',
                            overflowY: 'auto',
                        }}
                    >
                        {loading && (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '13px', color: GT_COLORS.textFaint }}>
                                    Searching...
                                </Typography>
                            </Box>
                        )}

                        {!loading && !hasResults && (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <SearchOffIcon sx={{ fontSize: 32, color: GT_COLORS.textDisabled }} />
                                <Typography sx={{ color: GT_COLORS.textMuted, fontSize: '13px', mt: 1 }}>
                                    No results for this search.
                                </Typography>
                            </Box>
                        )}

                        {!loading && groups.length > 0 && (
                            <>
                                <SectionHeader label="Groups" />
                                {groups.map((g) => (
                                    <Box
                                        key={g.id}
                                        onClick={() => {
                                            onSelectGroup?.(g);
                                            setOpen(false);
                                            setQuery('');
                                        }}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '10px 14px',
                                            cursor: 'pointer',
                                            '&:hover': { backgroundColor: GT_COLORS.hoverBg },
                                            '&:not(:last-child)': {
                                                borderBottom: `1px solid ${GT_COLORS.borderLight}`,
                                            },
                                        }}
                                    >
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                                {g.name}
                                            </Typography>
                                            <Typography sx={{ fontSize: '12px', color: GT_COLORS.textFaint }}>
                                                {g.teamCount} {g.teamCount === 1 ? 'team' : 'teams'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </>
                        )}

                        {!loading && people.length > 0 && (
                            <>
                                <SectionHeader label="People" hasBorderTop={groups.length > 0} />
                                {people.map((p) => (
                                    <Box
                                        key={p.id}
                                        onClick={() => {
                                            onSelectPerson?.(p);
                                            setOpen(false);
                                            setQuery('');
                                        }}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '10px 14px',
                                            cursor: 'pointer',
                                            '&:hover': { backgroundColor: GT_COLORS.hoverBg },
                                            '&:not(:last-child)': {
                                                borderBottom: `1px solid ${GT_COLORS.borderLight}`,
                                            },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                            <Avatar initials={p.initials} color={p.avatarColor} />
                                            <Box>
                                                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                                    {p.name}
                                                </Typography>
                                                <Typography sx={{ fontSize: '12px', color: GT_COLORS.textFaint }}>
                                                    {p.subtitle}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                            {p.unassigned ? (
                                                <Badge variant="amber">Unassigned</Badge>
                                            ) : (
                                                <>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '12px',
                                                            fontWeight: 500,
                                                            color: GT_COLORS.primary,
                                                        }}
                                                    >
                                                        {p.teamName}
                                                    </Typography>
                                                    {p.groupName && (
                                                        <Typography
                                                            sx={{ fontSize: '11px', color: GT_COLORS.textFaint }}
                                                        >
                                                            {p.groupName}
                                                        </Typography>
                                                    )}
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </>
                        )}

                        {!loading && teams.length > 0 && (
                            <>
                                <SectionHeader label="Teams" hasBorderTop={groups.length > 0 || people.length > 0} />
                                {teams.map((t) => (
                                    <Box
                                        key={t.id}
                                        onClick={() => {
                                            onSelectTeam?.(t);
                                            setOpen(false);
                                            setQuery('');
                                        }}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '10px 14px',
                                            cursor: 'pointer',
                                            '&:hover': { backgroundColor: GT_COLORS.hoverBg },
                                            '&:not(:last-child)': {
                                                borderBottom: `1px solid ${GT_COLORS.borderLight}`,
                                            },
                                        }}
                                    >
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                                {t.name}
                                            </Typography>
                                            <Typography sx={{ fontSize: '12px', color: GT_COLORS.textFaint }}>
                                                {t.memberCount} members &middot; {t.groupName}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </>
                        )}
                    </Box>
                )}
            </Box>
        </ClickAwayListener>
    );
};

// ─── Sub-components ─────────────────────────────────────────

const SectionHeader: React.FC<{ label: string; hasBorderTop?: boolean }> = ({ label, hasBorderTop }) => (
    <Box
        sx={{
            padding: '8px 14px',
            fontSize: '11px',
            fontWeight: 600,
            color: GT_COLORS.textFaint,
            textTransform: 'uppercase',
            letterSpacing: '.5px',
            backgroundColor: GT_COLORS.headerBg,
            borderBottom: `1px solid ${GT_COLORS.borderLight}`,
            ...(hasBorderTop && { borderTop: `1px solid ${GT_COLORS.borderLight}` }),
        }}
    >
        {label}
    </Box>
);

const Avatar: React.FC<{ initials: string; color: string; size?: number }> = ({
    initials,
    color,
    size = 32,
}) => (
    <Box
        sx={{
            width: size,
            height: size,
            borderRadius: '50%',
            backgroundColor: color,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            fontWeight: 600,
            flexShrink: 0,
        }}
    >
        {initials}
    </Box>
);

const Badge: React.FC<{ variant: 'amber' | 'green' | 'gray'; children: React.ReactNode }> = ({
    variant,
    children,
}) => {
    const styles = {
        amber: { bg: GT_COLORS.amberLight, color: GT_COLORS.amber },
        green: { bg: GT_COLORS.greenLight, color: GT_COLORS.green },
        gray: { bg: GT_COLORS.badgeGrayBg, color: GT_COLORS.badgeGrayColor },
    };
    const s = styles[variant];
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: 500,
                backgroundColor: s.bg,
                color: s.color,
            }}
        >
            {children}
        </Box>
    );
};
